import { io, type Socket } from 'socket.io-client';
import type { Dispatch } from 'react';
import type { RoomClientAction } from './roomReducer';
import type { RoomState } from '../types/room.types';
import type {
  HostAssignTeamsPayload,
  HostSetActivityPayload,
  HostSetPhasePayload,
  HostSetRoutingPayload,
  StudentInputPayload,
} from '../types/events.types';
import { ROOM_SOCKET_EVENTS } from '../types/events.types';
import { resolveInteractiveRoomUrl } from './resolveInteractiveRoomUrl';

const STORAGE_KEY = 'interactive_room_session';
const HOST_STORAGE_KEY = 'interactive_room_host_session';

export interface HostSessionStorage {
  roomId: string;
  joinCode: string;
  hostId: string;
}

export interface RoomSessionStorage {
  roomId: string;
  joinCode: string;
  participantId: string;
  sessionToken: string;
  /** Código async de evaluación (distinto del joinCode de sala) */
  accessCode?: string;
  sessionKind?: 'live' | 'async_exam';
}

export function loadStoredSession(): RoomSessionStorage | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveStoredSession(session: RoomSessionStorage) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearStoredSession() {
  localStorage.removeItem(STORAGE_KEY);
}

export function loadHostSession(): HostSessionStorage | null {
  try {
    const raw = localStorage.getItem(HOST_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveHostSession(session: HostSessionStorage) {
  localStorage.setItem(HOST_STORAGE_KEY, JSON.stringify(session));
}

export function clearHostSession() {
  localStorage.removeItem(HOST_STORAGE_KEY);
}

export interface RoomRejoinMeta {
  role: 'host' | 'student' | 'viewer';
  roomId: string;
  hostId?: string;
  participantId?: string;
  sessionToken?: string;
}

export interface SocketAck {
  ok?: boolean;
  error?: string;
  state?: RoomState;
  stateVersion?: number;
  [key: string]: unknown;
}

export class RoomSocketClient {
  private socket: Socket | null = null;
  private dispatch: Dispatch<RoomClientAction>;
  private baseUrl: string;
  private rejoinMeta: RoomRejoinMeta | null = null;
  private rejoinInFlight: Promise<boolean> | null = null;
  private roomJoined = false;

  constructor(dispatch: Dispatch<RoomClientAction>, baseUrl?: string) {
    this.dispatch = dispatch;
    this.baseUrl = baseUrl || this.readBaseUrl();
  }

  private readBaseUrl(): string {
    return resolveInteractiveRoomUrl();
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  connect(): void {
    if (this.socket?.connected) return;

    if (this.socket) {
      this.socket.connect();
      return;
    }

    this.socket = io(this.baseUrl, {
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 3000,
    });

    this.socket.on('connect', () => {
      this.dispatch({ type: 'SET_CONNECTION', payload: true });
      void this.rejoinIfNeeded();
    });

    this.socket.on('disconnect', () => {
      this.roomJoined = false;
      this.dispatch({ type: 'SET_CONNECTION', payload: false });
    });

    this.socket.on(ROOM_SOCKET_EVENTS.STATE_SYNC, (state: RoomState) => {
      this.dispatch({ type: 'STATE_SYNC', payload: state });
    });

    this.socket.on(ROOM_SOCKET_EVENTS.STATE_PATCH, (patchEvent) => {
      this.dispatch({ type: 'STATE_PATCH', payload: patchEvent });
    });

    this.socket.on(ROOM_SOCKET_EVENTS.PARTICIPANTS, (data: { participants: unknown[] }) => {
      if (Array.isArray(data.participants)) {
        this.dispatch({ type: 'SET_PARTICIPANTS', payload: data.participants as never });
      }
    });
  }

  isConnected(): boolean {
    return Boolean(this.socket?.connected);
  }

  /** Espera a que el socket esté listo antes de emitir eventos con ack. */
  private waitForConnection(timeoutMs = 10_000): Promise<boolean> {
    if (this.socket?.connected) return Promise.resolve(true);
    if (!this.socket) this.connect();

    return new Promise((resolve) => {
      const socket = this.socket;
      if (!socket) {
        resolve(false);
        return;
      }
      if (socket.connected) {
        resolve(true);
        return;
      }

      const timer = setTimeout(() => {
        socket.off('connect', onConnect);
        resolve(false);
      }, timeoutMs);

      const onConnect = () => {
        clearTimeout(timer);
        resolve(true);
      };

      socket.once('connect', onConnect);
    });
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.roomJoined = false;
    this.rejoinInFlight = null;
  }

  setRejoinMeta(meta: RoomRejoinMeta | null): void {
    this.rejoinMeta = meta;
    if (!meta) {
      this.roomJoined = false;
      return;
    }
    void this.ensureRoomMembership();
  }

  /** Garantiza socket conectado y unido al canal roomId (host/student/viewer). */
  async ensureRoomMembership(): Promise<boolean> {
    if (!this.rejoinMeta) return false;
    const connected = await this.waitForConnection();
    if (!connected) return false;
    if (this.roomJoined) return true;
    return this.rejoinIfNeeded();
  }

  private async rejoinIfNeeded(): Promise<boolean> {
    if (!this.rejoinMeta) return false;

    if (this.rejoinInFlight) {
      return this.rejoinInFlight;
    }

    const meta = this.rejoinMeta;
    this.rejoinInFlight = (async () => {
      const connected = await this.waitForConnection();
      if (!connected || !this.rejoinMeta) return false;

      const { role, roomId, hostId, participantId, sessionToken } = meta;

      if (role === 'host' && hostId) {
        const ack = await this.hostJoinRoom(roomId, hostId);
        if (ack.ok) {
          this.roomJoined = true;
          this.requestSync(roomId);
          return true;
        }
        return false;
      }

      if (role === 'student' && participantId) {
        const ack = await this.studentJoinRoom({ roomId, participantId, sessionToken });
        if (ack.ok) {
          this.roomJoined = true;
          this.requestSync(roomId);
          return true;
        }
        return false;
      }

      if (role === 'viewer') {
        const ack = await this.viewerJoinRoom(roomId);
        if (ack.ok) {
          this.roomJoined = true;
          this.requestSync(roomId);
          return true;
        }
        return false;
      }

      return false;
    })();

    try {
      return await this.rejoinInFlight;
    } finally {
      this.rejoinInFlight = null;
    }
  }

  requestSync(roomId: string): void {
    this.socket?.emit(ROOM_SOCKET_EVENTS.STATE_REQUEST_SYNC, { roomId });
  }

  async createRoom(hostId: string, title?: string) {
    const resp = await fetch(`${this.baseUrl}/api/rooms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hostId, title }),
    });
    const json = await resp.json();
    if (!resp.ok) throw new Error(json.error || 'create_room_failed');
    return json as {
      roomId: string;
      joinCode: string;
      stateVersion: number;
      state: RoomState;
    };
  }

  async lookupRoom(joinCode: string) {
    const resp = await fetch(`${this.baseUrl}/api/rooms/lookup/${encodeURIComponent(joinCode)}`);
    if (!resp.ok) throw new Error('room_not_found');
    return resp.json() as Promise<{
      roomId: string;
      joinCode: string;
      stateVersion: number;
      state: import('../types/room.types').RoomState;
    }>;
  }

  async joinRoomByCode(params: {
    joinCode: string;
    displayName: string;
    studentId?: string;
    identityKey?: string;
  }) {
    const resp = await fetch(`${this.baseUrl}/api/rooms/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    const json = await resp.json();
    if (!resp.ok) throw new Error(json.error || 'join_failed');
    return json as {
      roomId: string;
      joinCode: string;
      participantId: string;
      sessionToken: string;
      stateVersion: number;
      state: RoomState;
    };
  }

  hostJoinRoom(roomId: string, hostId: string): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_JOIN, { roomId, hostId });
  }

  studentJoinRoom(payload: {
    roomId?: string;
    joinCode?: string;
    sessionToken?: string;
    participantId?: string;
    displayName?: string;
    studentId?: string;
  }): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.STUDENT_JOIN, payload);
  }

  viewerJoinRoom(roomId: string): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.VIEWER_JOIN, { roomId });
  }

  hostSetPhase(payload: HostSetPhasePayload): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_SET_PHASE, payload);
  }

  hostSetActivity(payload: HostSetActivityPayload): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_SET_ACTIVITY, payload);
  }

  hostSetRouting(payload: HostSetRoutingPayload): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_SET_ROUTING, payload);
  }

  hostAssignTeams(payload: HostAssignTeamsPayload): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_ASSIGN_TEAMS, payload);
  }

  hostAction(payload: {
    roomId: string;
    hostId: string;
    action: string;
    data?: unknown;
  }): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_ACTION, payload);
  }

  hostEndRoom(roomId: string, hostId: string): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_END, { roomId, hostId });
  }

  studentInput(payload: StudentInputPayload): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.STUDENT_INPUT, payload);
  }

  studentReportBreach(payload: {
    roomId: string;
    participantId: string;
    sessionToken?: string;
    reason?: 'visibility' | 'blur' | 'fullscreen_exit';
  }): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.STUDENT_BREACH, payload);
  }

  hostSetAssessmentMode(payload: {
    roomId: string;
    hostId: string;
    enabled: boolean;
  }): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_SET_ASSESSMENT, payload);
  }

  hostUnlockParticipant(payload: {
    roomId: string;
    hostId: string;
    participantId: string;
  }): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_UNLOCK_PARTICIPANT, payload);
  }

  hostLaunchExam(payload: { roomId: string; hostId: string; exam: unknown }): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_LAUNCH_EXAM, payload);
  }

  hostAdvanceExamBlock(payload: { roomId: string; hostId: string }): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_ADVANCE_EXAM_BLOCK, payload);
  }

  hostEndExam(payload: { roomId: string; hostId: string }): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_END_EXAM, payload);
  }

  hostConfirmExamReview(payload: {
    roomId: string;
    hostId: string;
    participantId: string;
    blockId: string;
  }): Promise<SocketAck> {
    return this.emitAck(ROOM_SOCKET_EVENTS.HOST_CONFIRM_EXAM_REVIEW, payload);
  }

  private async emitAck(event: string, payload: unknown): Promise<SocketAck> {
    const connected = await this.waitForConnection();
    if (!connected || !this.socket) {
      return { ok: false, error: 'not_connected' };
    }

    return new Promise((resolve) => {
      this.socket!.emit(event, payload, (ack: SocketAck) => {
        resolve({
          ...ack,
          ok: ack?.ok !== false,
        });
      });
    });
  }
}
